import React, {useEffect, useRef, useState, useCallback} from 'react'
import axios from "axios";
import SimplePeer from 'simple-peer'
import Swal from "sweetalert2";
import {useBaseUrl} from "../utils/utils";

/**
 * Previous version signaled over REST polling (POST communications/api/signal/:room_id)
 * against endpoints that live in a different app (uzima_communications) than
 * the WebSocket consumer that was actually built for this (uzima_comms) -
 * the two halves were never wired to each other. This version speaks the
 * uzima_comms.consumers.MeetingConsumer protocol directly over a real
 * WebSocket: join a meeting room by its meet_id (+ passcode for closed
 * meetings), get the roster of who's already there, offer to each of them,
 * and relay whatever simple-peer's own 'signal' event produces - it already
 * encodes offer/answer/ICE-candidate, so the wire protocol only needs one
 * generic 'signal' message kind rather than three.
 */

type RosterEntry = { participant_id: number; participant_class: string };

type Props = {
    meetId: string;
    passcode?: string;
    callType?: 'audio' | 'video';
    onEnd?: () => void;
};

const getStoredToken = () =>
    localStorage.getItem('token') || localStorage.getItem('authToken') || sessionStorage.getItem('token') || '';

const VideoChat: React.FC<Props> = ({meetId, passcode, callType = 'video', onEnd}) => {
    const baseUrl = useBaseUrl();
    const [connected, setConnected] = useState(false);
    const [muted, setMuted] = useState(false);
    const [remoteStreams, setRemoteStreams] = useState<Record<number, MediaStream>>({});
    const [error, setError] = useState<string>('');

    const localVideoRef = useRef<HTMLVideoElement>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const localStreamRef = useRef<MediaStream | null>(null);
    const peersRef = useRef<Map<number, SimplePeer.Instance>>(new Map());
    const selfIdRef = useRef<number | null>(null);
    const iceServersRef = useRef<RTCIceServer[]>([{urls: 'stun:stun.l.google.com:19302'}]);

    const cleanupPeer = useCallback((participantId: number) => {
        const peer = peersRef.current.get(participantId);
        if (peer) {
            peer.destroy();
            peersRef.current.delete(participantId);
        }
        setRemoteStreams((prev) => {
            const next = {...prev};
            delete next[participantId];
            return next;
        });
    }, []);

    const createPeer = useCallback((participantId: number, initiator: boolean) => {
        const peer = new SimplePeer({
            initiator,
            trickle: true,
            stream: localStreamRef.current || undefined,
            config: {iceServers: iceServersRef.current},
        });

        peer.on('signal', (data) => {
            wsRef.current?.send(JSON.stringify({
                kind: 'signal',
                target_participant_id: participantId,
                signal: data,
            }));
        });

        peer.on('stream', (remoteStream) => {
            setRemoteStreams((prev) => ({...prev, [participantId]: remoteStream}));
        });

        peer.on('error', () => cleanupPeer(participantId));
        peer.on('close', () => cleanupPeer(participantId));

        peersRef.current.set(participantId, peer);
        return peer;
    }, [cleanupPeer]);

    useEffect(() => {
        let cancelled = false;

        const start = async () => {
            const token = getStoredToken();
            if (!token) {
                setError('You must be signed in to join a call.');
                return;
            }

            try {
                const turnResponse = await axios.get(`${baseUrl}/comms/turn-credentials`, {
                    headers: {Authorization: `Bearer ${token}`},
                });
                if (turnResponse.data?.data?.ice_servers) {
                    iceServersRef.current = turnResponse.data.data.ice_servers;
                }
            } catch {
                // STUN-only default already set - a call can still succeed
                // on networks that don't need a relay.
            }

            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: callType === 'video',
                    audio: true,
                });
                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                localStreamRef.current = stream;
                if (localVideoRef.current) {
                    localVideoRef.current.srcObject = stream;
                }
            } catch (e) {
                setError('Could not access camera/microphone: ' + e);
                return;
            }

            const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
            const wsHost = baseUrl.replace(/^https?:\/\//, '');
            const params = new URLSearchParams({token, ...(passcode ? {passcode} : {})});
            const ws = new WebSocket(`${wsProtocol}://${wsHost}/ws/comms/meetings/${meetId}/?${params.toString()}`);
            wsRef.current = ws;

            ws.onopen = () => {
                setConnected(true);
                ws.send(JSON.stringify({kind: 'join'}));
            };

            ws.onclose = () => {
                setConnected(false);
            };

            ws.onerror = () => {
                setError('Could not connect to the meeting. It may have ended or the link is invalid.');
            };

            ws.onmessage = (event) => {
                const msg = JSON.parse(event.data);

                switch (msg.kind) {
                    case 'roster':
                        selfIdRef.current = msg.self_id;
                        (msg.participants as RosterEntry[]).forEach((p) => {
                            createPeer(p.participant_id, true);
                        });
                        break;

                    case 'signal': {
                        const fromId = msg.from_participant_id;
                        if (fromId === selfIdRef.current) break;
                        // Ignore signals addressed to someone else - Channels
                        // groups have no per-member addressing, so every
                        // relayed message reaches every socket in the room
                        // and each client filters by target_participant_id.
                        if (msg.target_participant_id && msg.target_participant_id !== selfIdRef.current) break;

                        let peer = peersRef.current.get(fromId);
                        if (!peer) {
                            peer = createPeer(fromId, false);
                        }
                        peer.signal(msg.signal);
                        break;
                    }

                    case 'participant_left':
                        cleanupPeer(msg.participant_id);
                        break;

                    case 'participant_joined':
                        // The newcomer offers first (handled via their own
                        // 'roster' response) - existing participants just
                        // wait for that offer to arrive as a 'signal'.
                        break;

                    default:
                        break;
                }
            };
        };

        start();

        return () => {
            cancelled = true;
            wsRef.current?.send(JSON.stringify({kind: 'leave'}));
            wsRef.current?.close();
            peersRef.current.forEach((peer) => peer.destroy());
            peersRef.current.clear();
            localStreamRef.current?.getTracks().forEach((t) => t.stop());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meetId, passcode, callType, baseUrl, createPeer, cleanupPeer]);

    const toggleMute = () => {
        const stream = localStreamRef.current;
        if (!stream) return;
        const nextMuted = !muted;
        stream.getAudioTracks().forEach((track) => {
            track.enabled = !nextMuted;
        });
        setMuted(nextMuted);
        wsRef.current?.send(JSON.stringify({kind: 'mute_self', muted: nextMuted}));
    };

    const hangUp = () => {
        wsRef.current?.send(JSON.stringify({kind: 'hangup'}));
        wsRef.current?.close();
        onEnd?.();
    };

    if (error) {
        return <div className="video-comm-error">{error}</div>;
    }

    return (
        <div className="video-comm-room">
            <div className="video-comm-status">{connected ? 'Connected' : 'Connecting…'}</div>
            <div className="video-comm-grid">
                <video ref={localVideoRef} autoPlay muted playsInline style={{width: '260px', height: '180px'}}/>
                {Object.entries(remoteStreams).map(([participantId, stream]) => (
                    <RemoteVideo key={participantId} stream={stream}/>
                ))}
            </div>
            <div className="video-comm-controls">
                <button onClick={toggleMute}>{muted ? 'Unmute' : 'Mute'}</button>
                <button onClick={hangUp}>Leave call</button>
            </div>
        </div>
    );
};

const RemoteVideo: React.FC<{ stream: MediaStream }> = ({stream}) => {
    const ref = useRef<HTMLVideoElement>(null);
    useEffect(() => {
        if (ref.current) {
            ref.current.srcObject = stream;
        }
    }, [stream]);
    return <video ref={ref} autoPlay playsInline style={{width: '260px', height: '180px'}}/>;
};

export default VideoChat;
